import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Paper,
  Rating,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import {
  PublicProfileProvider,
  usePublicProfile,
} from '../context/PublicProfileContext';
import GalleryCarousel from './components/GalleryCarousel';
import ShopBarberProfileModal from './components/ShopBarberProfileModal';
import PublicAmenitiesList from './components/PublicAmenitiesList';
import { CreateBookingSlot } from '../types/bookingSlot';
import { ShopBarber } from '../types/shop';
import { getOpenStatus } from '../utils/businessHoursUtils';
import { getMockRatingSummary } from '../types/review';
import {
  BookableService,
  Booking,
  BookingContactDetails,
} from '../types/booking';
import { createBooking } from '../middleware/reservation_service/createRequest';
import { darkPageBg, darkCardBg } from './components/darkFormStyles';
import { companyName } from '../defaults/AppDefaults';
import StickyFooter from './components/StickyFooter';
import SampleDataBadge from './components/SampleDataBadge';
import BookingModal from './components/BookingModal';

const accent = '#dd8709';

const PublicShopProfileContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    error,
    shop,
    galleryImages,
    shopServices,
    completedBookingsCount,
    getSlots,
    removeSlotFromCache,
  } = usePublicProfile();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preselected, setPreselected] = useState<BookableService | null>(null);
  const [bookingBarberId, setBookingBarberId] = useState<bigint | null>(null);
  const [selectedShopBarber, setSelectedShopBarber] =
    useState<ShopBarber | null>(null);

  // Only shown when arriving via a click-through from the Find Barbers
  // search results page, so users can easily return to their results.
  const cameFromSearch = Boolean(
    (location.state as { fromSearch?: boolean } | null)?.fromSearch
  );

  const openStatus = useMemo(
    () => getOpenStatus(undefined, shop?.openingHours, shop?.closingHours),
    [shop]
  );

  const ratingSummary = useMemo(
    () => (shop?.id ? getMockRatingSummary(shop.id) : null),
    [shop?.id]
  );

  const services: BookableService[] = useMemo(
    () =>
      shopServices.map(({ service, barber }) => ({
        service,
        barberId: barber.id,
        barberName: `${barber.firstName || ''}`.trim(),
        barberEmail: barber.email,
      })),
    [shopServices]
  );

  const address = [
    shop?.addressLineOne,
    shop?.addressLineTwo,
    shop?.city,
    shop?.postcode,
  ]
    .filter(Boolean)
    .join(', ');

  const bookingServices = useMemo(
    () =>
      bookingBarberId !== null
        ? services.filter((s) => s.barberId === bookingBarberId)
        : services,
    [services, bookingBarberId]
  );

  const openBookingModal = (service?: BookableService, barberId?: bigint) => {
    setPreselected(service ?? null);
    setBookingBarberId(barberId ?? null);
    setBookingOpen(true);
  };

  const openShopBarberProfile = (shopBarber: ShopBarber) => {
    setSelectedShopBarber(shopBarber);
  };

  const handleBookWithShopBarber = (service?: BookableService) => {
    const barberId = selectedShopBarber?.id;
    setSelectedShopBarber(null);
    openBookingModal(service, barberId);
  };

  const handleConfirmBooking = async (
    slot: CreateBookingSlot,
    bookable: BookableService,
    contact: BookingContactDetails
  ) => {
    const reservationData: Booking = {
      slot_id: slot.id,
      customer_email: contact.customerEmail,
      customer_phone: contact.customerPhone,
      customer_name: contact.customerName,
      barber_name: bookable.barberName,
      barber_email_address: bookable.barberEmail,
      service_name: bookable.service.name,
      price: bookable.service.price,
      status: 'PENDING',
      type: 'SHOP',
      shop_id: shop?.id,
      barber_id: slot.barber_id,
    };

    await createBooking(reservationData);
    removeSlotFromCache(slot.id);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: darkPageBg,
        }}
      >
        <CircularProgress sx={{ color: accent }} />
      </Box>
    );
  }

  if (error || !shop) {
    return (
      <Container sx={{ mt: '100px', pb: 6 }}>
        <Alert severity="error">
          {error || 'This shop profile could not be found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', color: '#fff' }}>
      {/* Cover / Header */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 220, sm: 300 },
          backgroundImage: `linear-gradient(rgba(8,9,12,0.5), rgba(8,9,12,0.85)), url(${
            shop.shopProfileImageLink || ''
          })`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mt: '64px',
        }}
      >
        {cameFromSearch && (
          <Button
            onClick={() => navigate('/find-barbers')}
            startIcon={<ArrowBackIcon />}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            Back to Results
          </Button>
        )}
        <Container
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            pb: 3,
          }}
        >
          <Avatar
            src={shop.shopProfileImageLink}
            sx={{ width: 96, height: 96, border: '3px solid #fff', mr: 2 }}
          >
            <StorefrontIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {shop.name || 'Barbershop'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              {address && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">{address}</Typography>
                </Box>
              )}
              {shop.openingHours && shop.closingHours && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="body2">
                    {shop.openingHours} - {shop.closingHours}
                  </Typography>
                </Box>
              )}
              <Chip
                size="small"
                label={openStatus.isOpen ? 'Open Now' : 'Closed'}
                sx={{
                  bgcolor: openStatus.isOpen ? '#2e7d32' : '#616161',
                  color: '#fff',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Gallery
            </Typography>
            <GalleryCarousel images={galleryImages} />

            {shop.amenities && (
              <>
                <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Amenities
                </Typography>
                <PublicAmenitiesList amenities={shop.amenities} />
              </>
            )}

            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {services.length === 0 && (
                <Typography color="rgba(255,255,255,0.6)">
                  No services have been added yet.
                </Typography>
              )}
              {services.map((bookable, i) => (
                <Paper
                  key={`${bookable.service.id}-${i}`}
                  sx={{
                    p: 2,
                    bgcolor: darkCardBg,
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      fontWeight="bold"
                      sx={{ color: 'rgba(255,255,255,0.8)' }}
                    >
                      {bookable.service.name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mt: 0.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AttachMoneyIcon
                          fontSize="small"
                          sx={{ color: accent }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          £{bookable.service.price ?? 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ color: accent }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {bookable.service.duration ?? 0} min
                        </Typography>
                      </Box>
                      {bookable.barberId !== undefined && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PersonIcon fontSize="small" sx={{ color: accent }} />
                          <MuiLink
                            component="button"
                            type="button"
                            underline="hover"
                            sx={{ color: '#fff' }}
                            onClick={() => {
                              const shopBarber = (shop.shopBarbers || []).find(
                                (b) => b.id === bookable.barberId
                              );
                              if (shopBarber) openShopBarberProfile(shopBarber);
                            }}
                          >
                            {bookable.barberName}
                          </MuiLink>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => openBookingModal(bookable)}
                    sx={{ bgcolor: accent, '&:hover': { bgcolor: '#b86e08' } }}
                  >
                    Select & Book
                  </Button>
                </Paper>
              ))}
            </Box>

            {(shop.shopBarbers || []).length > 0 && (
              <>
                <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Meet the Team
                </Typography>
                <Grid container spacing={2}>
                  {(shop.shopBarbers || []).map((shopBarber) => (
                    <Grid item xs={12} sm={6} key={shopBarber.id?.toString()}>
                      <Paper
                        sx={{
                          bgcolor: darkCardBg,
                          border: '1px solid rgba(255,255,255,0.08)',
                          overflow: 'hidden',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(221,135,9,0.2)',
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => openShopBarberProfile(shopBarber)}
                          aria-label={`View profile for ${
                            `${shopBarber.firstName || ''}`.trim() || 'barber'
                          }`}
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 2,
                          }}
                        >
                          <Avatar src={shopBarber.profileImageSelfLink}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography
                              fontWeight="bold"
                              sx={{ color: 'rgba(255,255,255,0.8)' }}
                            >
                              {`${shopBarber.firstName || ''}`.trim()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'rgba(255,255,255,0.6)' }}
                            >
                              {(shopBarber.barberShopServices || []).length}{' '}
                              services offered
                            </Typography>
                          </Box>
                        </CardActionArea>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                bgcolor: darkCardBg,
                border: '1px solid rgba(255,255,255,0.08)',
                position: { md: 'sticky' },
                top: { md: 90 },
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <EventAvailableIcon sx={{ color: accent }} />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  {completedBookingsCount} completed{' '}
                  {completedBookingsCount <= 1 ? 'booking' : 'bookings'}
                </Typography>
              </Box>

              {ratingSummary && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Rating
                    value={ratingSummary.averageRating}
                    precision={0.1}
                    readOnly
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    {ratingSummary.averageRating.toFixed(1)} (
                    {ratingSummary.totalReviews} reviews)
                  </Typography>
                  <SampleDataBadge />
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => openBookingModal()}
                sx={{
                  bgcolor: accent,
                  '&:hover': { bgcolor: '#b86e08' },
                  mb: 2,
                }}
              >
                Book Now
              </Button>

              {ratingSummary && ratingSummary.reviews.length > 0 && (
                <>
                  <Divider
                    sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'rgba(255,255,255,0.8)' }}
                    >
                      Recent reviews
                    </Typography>
                    <SampleDataBadge />
                  </Box>
                  {ratingSummary.reviews.map((review) => (
                    <Box key={review.id} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Rating value={review.rating} size="small" readOnly />
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          {review.authorName}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {review.comment}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        title={
          bookingBarberId !== null && bookingServices[0]?.barberName
            ? `Book with ${bookingServices[0].barberName}`
            : `Book with ${shop.name || 'this shop'}`
        }
        services={bookingServices}
        preselectedService={preselected}
        loadSlots={getSlots}
        onConfirm={handleConfirmBooking}
      />

      <ShopBarberProfileModal
        open={!!selectedShopBarber}
        onClose={() => setSelectedShopBarber(null)}
        barber={selectedShopBarber}
        services={services}
        onBook={handleBookWithShopBarber}
      />
    </Box>
  );
};

const PublicShopProfile: React.FC = () => {
  const { shopId: shopIdParam } = useParams<{ shopId: string }>();
  const shopId = (() => {
    try {
      return shopIdParam ? BigInt(shopIdParam) : null;
    } catch {
      return null;
    }
  })();

  if (!shopId) {
    return (
      <Container sx={{ mt: '100px', pb: 6 }}>
        <Alert severity="error">Invalid shop profile link.</Alert>
      </Container>
    );
  }

  return (
    <PublicProfileProvider entityType="SHOP" id={shopId}>
      <PublicShopProfileContent />
      <StickyFooter {...companyName} />
    </PublicProfileProvider>
  );
};

export default PublicShopProfile;
